import * as React from 'react';
import type { GDSStyleObject } from '@leagueplatform/genesis-core';
import type {
  ConfigEntityDetail,
  EntityDetail,
  DomainEntity,
  ColumnRendererType,
} from '@web-config-app/core';

import type { BulkActionMenuItem } from '@web-config-app/core-react';
import { EntityListTableHeaders } from '../entity-list-table-headers/entity-list-table-headers.component';
import { Table, TableRow } from '../table-pieces.component';
import { EntityListTableSkeleton } from '../entity-list-table-skeleton/entity-list-table-skeleton.component';
import { useEntityListTableColumns } from '../use-entity-list-table-columns';
import { EntityListTableBulkActions } from '../entity-list-table-bulk-actions/entity-list-table-bulk-actions.component';
import { isDynamicColumn } from '../is-dynamic-column';
import { getValueAtPath } from '../get-value-at-path';
import type { ColumnRenderer } from '../cell-renderers/renderer-registry';
import type {
  EntityListTableSelectionMode,
  EntityListTableSelectionProps,
} from '../../../types/components.types';

type EntityItem = ConfigEntityDetail | EntityDetail;

/**
 * Returns the CSS for table headings based on selection state.
 * When items are selected, the background color changes to indicate active selection.
 */
export const getTableHeadingCss = (anySelected: boolean): GDSStyleObject => ({
  backgroundColor: anySelected
    ? '$surfaceBackgroundSecondary'
    : '$surfaceBackgroundPrimary',
});

export interface RowContext {
  linkTo?: string;
  itemId?: string;
  disabled?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  rowCss?: GDSStyleObject;
}

export interface EntityListTableInternalProps {
  entityData?: EntityItem[];
  isLoading?: boolean;
  domainEntity: DomainEntity;
  getItemName: (data: EntityItem) => string | undefined;
  selectionMode?: EntityListTableSelectionMode;
  selectionProps?: EntityListTableSelectionProps<EntityItem>;
  bulkActions?: BulkActionMenuItem[];
  /**
   * Called for each row to compute row-specific context (link URL, selection state, etc.)
   */
  getRowContext: (item: EntityItem) => RowContext;
  /**
   * The renderer registry to use for cell rendering
   */
  registry: Record<ColumnRendererType, ColumnRenderer>;
}

export const EntityListTable: React.FC<EntityListTableInternalProps> = ({
  entityData,
  isLoading = false,
  domainEntity,
  getItemName,
  selectionMode = 'none',
  selectionProps,
  bulkActions,
  getRowContext,
  registry,
}) => {
  const { headerColumns, dataColumns } = useEntityListTableColumns({
    columns: domainEntity.listTable?.columns,
    selectionMode,
  });

  const { onSelectAllChange, allSelected, someSelected, selectedCount } =
    selectionProps ?? {
      selectedEntities: [],
      allSelected: false,
      someSelected: false,
      selectedCount: 0,
      onSelectAllChange: () => false,
    };

  const anySelected = Boolean(allSelected || someSelected);
  const tableHeadingCss = getTableHeadingCss(anySelected);

  return (
    <>
      {selectionMode === 'multi' && bulkActions && bulkActions.length > 0 && (
        <EntityListTableBulkActions
          bulkActions={bulkActions}
          selectedCount={selectedCount ?? 0}
          isVisible={anySelected}
        />
      )}
      <Table>
        <EntityListTableHeaders
          columns={headerColumns}
          selectionMode={selectionMode}
          tableHeadingCss={tableHeadingCss}
          selectionState={{
            allSelected: allSelected ?? false,
            indeterminate: someSelected ?? false,
            onSelectAllChange: onSelectAllChange ?? (() => false),
            disabled: isLoading,
          }}
        />
        {isLoading ? (
          <EntityListTableSkeleton
            columnsLength={headerColumns.length}
            selectable={selectionMode !== 'none'}
          />
        ) : (
          <tbody>
            {entityData?.map((item) => {
              const rowContext = getRowContext(item);
              return (
                <TableRow key={item.id} css={rowContext.rowCss}>
                  {dataColumns.map((column) => {
                    if (isDynamicColumn(column)) {
                      const rawValue = getValueAtPath(item, column.path);
                      const value = rawValue ?? '';
                      const rendererKey = column.renderer ?? 'text';
                      const Renderer = registry[rendererKey] || registry.text;

                      return (
                        <Renderer
                          key={`${item.id}-${column.id}`}
                          item={item}
                          value={value}
                          col={column}
                          linkTo={rowContext.linkTo}
                          getItemName={getItemName}
                        />
                      );
                    }

                    const Renderer = registry[column.key];
                    return (
                      <Renderer
                        key={`${item.id}-${column.key}`}
                        item={item}
                        linkTo={rowContext.linkTo}
                        getItemName={getItemName}
                        itemId={rowContext.itemId}
                        onChange={rowContext.onSelect}
                        disabled={rowContext.disabled}
                        selected={rowContext.selected}
                      />
                    );
                  })}
                </TableRow>
              );
            })}
          </tbody>
        )}
      </Table>
    </>
  );
};
